import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, ChevronDown, CheckCircle2, AlertCircle, FlaskConical, Receipt, Sparkles, ShieldCheck } from 'lucide-react';
import { trackEvent } from '../../lib/analytics';
import { PH_REGIONS, provincesOf, citiesOf } from '../../data/phGeo';

const productSizes = {
  'FlexiOss Ortho': ['1 cm', '3 cm', '5 cm', 'Custom (up to 7 cm)'],
  'FlexiOss Dent': ['1 cm', '3 cm', '5 cm'],
  'FlexiOss Vet': ['0.2 cm (3 pcs/pack)', '0.5 cm (3 pcs/pack)', '1 cm (1 pc/pack)', '3 cm (1 pc/pack)', '5 cm (1 pc/pack)'],
};

const specialtyMap = {
  'FlexiOss Ortho': 'Orthopedics',
  'FlexiOss Dent': 'Dentistry / Maxillofacial',
  'FlexiOss Vet': 'Veterinary Medicine',
};

const productColors = {
  'FlexiOss Ortho': '#3b82f6',
  'FlexiOss Dent': '#E91E8C',
  'FlexiOss Vet': '#4CAF50',
};

function buildMessage(product, size, pieces, wantSamples, wantPricing, note) {
  const parts = [];
  if (note) parts.push(note);
  if (wantSamples) {
    let s = `I would like to request a free clinical sample${product ? ` of ${product}` : ''}`;
    if (size) s += `, size: ${size}`;
    if (pieces) s += `, quantity: ${pieces} piece(s)`;
    parts.push(s + '.');
  }
  if (wantPricing) {
    parts.push(`Please send pricing and a formal quotation${product ? ` for ${product}` : ''}${size ? ` (size: ${size})` : ''}.`);
  }
  if (parts.length === 0) return '';
  parts.push('Kindly include availability and delivery details for the Philippines.');
  return parts.join('\n\n');
}

const initialForm = {
  name: '', email: '', phone: '', clinic: '', specialty: '', specialtyOther: '', product: '', size: '', pieces: '', message: '',
  region: '', province: '', city: '', postal_code: '', street_address: '',
  requestSamples: true, requestPricing: false,
  consent_nda: false, consent_privacy: false, consent_clinical_report: false,
};

const inputCls = "w-full bg-slate-900/80 border border-slate-600 rounded-none px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 outline-none transition-all";
const selectCls = `${inputCls} appearance-none`;
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-1.5";

const RequestToggle = ({ active, onClick, icon, title, sub, badge, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-pressed={active}
    className={`relative flex w-full flex-col items-start gap-1 rounded-none border-2 p-3 text-left transition-all ${
      active
        ? 'border-amber-400 bg-amber-400/15 shadow-[0_0_20px_-6px_rgba(251,191,36,0.6)]'
        : 'border-slate-600/70 bg-slate-900/60 hover:border-amber-400/50'
    }`}
  >
    {badge && (
      <span className="absolute -top-2 right-2 rounded-full bg-amber-400 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-900">{badge}</span>
    )}
    <span className={`flex items-center gap-2 text-sm font-bold ${active ? 'text-amber-200' : 'text-slate-200'}`}>
      <span className={`flex h-7 w-7 items-center justify-center rounded-none ${active ? 'bg-amber-400/25 text-amber-300' : 'bg-slate-800 text-slate-400'}`}>{icon}</span>
      {title}
    </span>
    <span className="text-[11px] leading-tight text-slate-400 pl-9">{sub}</span>
  </button>
);

const ContactModal = ({ isOpen, onClose, initialIntent = 'samples' }) => {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState('idle');

  // Auto-update specialty and message when product/size/pieces/request type change
  useEffect(() => {
    const autoSpecialty = specialtyMap[formData.product] || formData.specialty;
    const autoMessage = buildMessage(formData.product, formData.size, formData.pieces, formData.requestSamples, formData.requestPricing);
    setFormData(prev => ({ ...prev, specialty: autoSpecialty, message: autoMessage }));
  }, [formData.product, formData.size, formData.pieces, formData.requestSamples, formData.requestPricing]);

  // Reset size when product changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, size: '', pieces: '' }));
  }, [formData.product]);

  // On open: apply the requested intent (samples / pricing) + track
  useEffect(() => {
    if (!isOpen) return;
    trackEvent('contact_modal_open', { intent: initialIntent });
    setFormData(prev => ({
      ...prev,
      requestSamples: initialIntent === 'samples',
      requestPricing: initialIntent === 'pricing',
    }));
  }, [isOpen, initialIntent]);

  // Close on Escape key (keyboard accessibility)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Lock page scroll while the modal is open (stop Lenis + freeze body) so the
  // wheel scrolls the dialog, not the page behind it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const lenis = typeof window !== 'undefined' ? window.__lenis : null;
    lenis?.stop();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      lenis?.start();
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'region') { next.province = ''; next.city = ''; }
      if (name === 'province') { next.city = ''; }
      return next;
    });
  };

  const toggle = (key) => setFormData(prev => ({ ...prev, [key]: !prev[key] }));

  const requestTypes = [];
  if (formData.requestSamples) requestTypes.push('Free Clinical Sample');
  if (formData.requestPricing) requestTypes.push('Pricing / Quotation');
  const requestType = requestTypes.join(' + ');
  const noneSelected = requestTypes.length === 0;
  const consentBlocked = !formData.consent_nda || !formData.consent_privacy || (formData.requestSamples && !formData.consent_clinical_report);
  const submitDisabled = status === 'loading' || noneSelected || consentBlocked;
  const resolvedSpecialty = formData.specialty === 'Other' ? (formData.specialtyOther || 'Other') : formData.specialty;
  const submitLabel = requestTypes.length === 1
    ? (formData.requestSamples ? 'Request Free Clinical Sample' : 'Request Pricing')
    : 'Submit Inquiry';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (noneSelected) return;
    setStatus('loading');
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name, email: formData.email, phone: formData.phone,
          clinic: formData.clinic, specialty: resolvedSpecialty, product: formData.product,
          size: formData.size, pieces: formData.pieces, message: formData.message,
          request_type: requestType,
          region: formData.region, province: formData.province, city: formData.city,
          postal_code: formData.postal_code, street_address: formData.street_address,
          consent_nda: formData.consent_nda,
          consent_privacy: formData.consent_privacy, consent_clinical_report: formData.consent_clinical_report,
        }),
      });
      if (!res.ok) throw new Error('Failed');
      trackEvent('generate_lead', { request_type: requestType, product: formData.product || 'N/A', specialty: resolvedSpecialty || 'N/A', region: formData.region || 'N/A' });
      setStatus('success');
      setTimeout(() => { setStatus('idle'); onClose(); setFormData(initialForm); }, 2500);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const sizes = productSizes[formData.product] || [];
  const accentColor = productColors[formData.product] || '#3b82f6';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div data-testid="contact-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
          <motion.div data-lenis-prevent data-testid="contact-modal" role="dialog" aria-modal="true" aria-label="Request Samples and Pricing" initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ duration: 0.25 }} className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 rounded-none shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto overscroll-contain transition-colors duration-300" style={{ borderColor: formData.product ? accentColor : '#334155' }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-gradient-to-br from-slate-800 to-slate-900 z-10">
              <div>
                <h3 className="font-display text-2xl font-bold text-white">Request Samples &amp; Pricing</h3>
                <p className="text-slate-400 text-sm mt-0.5">Our team will respond within 24 hours</p>
              </div>
              <button onClick={onClose} data-testid="contact-modal-close" className="text-slate-400 hover:text-white transition-colors p-1"><X className="w-5 h-5" /></button>
            </div>

            {/* Success */}
            {status === 'success' ? (
              <div className="px-6 py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-blue-400" /></div>
                <h4 data-testid="contact-success-message" className="text-xl font-bold text-white">Inquiry Submitted!</h4>
                <p className="text-slate-400 mt-2 text-sm">A sales representative will contact you shortly.</p>
              </div>
            ) : status === 'error' ? (
              <div className="px-6 py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4"><AlertCircle className="w-8 h-8 text-red-400" /></div>
                <h4 className="text-xl font-bold text-white">Something went wrong</h4>
                <p className="text-slate-400 mt-2 text-sm">Please try again or email us at fms.medequip@gmail.com</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
                {/* Highlighted: What to request */}
                <div data-testid="request-type-box" className="rounded-none border-2 border-amber-400/50 bg-gradient-to-br from-amber-400/10 to-amber-500/[0.04] p-4 shadow-[0_0_28px_-10px_rgba(251,191,36,0.5)]">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" /> What can we help you with? *
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <RequestToggle
                      active={formData.requestSamples}
                      onClick={() => toggle('requestSamples')}
                      icon={<FlaskConical className="w-4 h-4" />}
                      title="Free Clinical Sample"
                      sub="Request clinical test units"
                      testid="request-samples-toggle"
                    />
                    <RequestToggle
                      active={formData.requestPricing}
                      onClick={() => toggle('requestPricing')}
                      icon={<Receipt className="w-4 h-4" />}
                      title="Pricing & Quote"
                      sub="Get a quotation"
                      badge="Popular"
                      testid="request-pricing-toggle"
                    />
                  </div>
                  {noneSelected && (
                    <p className="text-[11px] text-amber-300/90 mt-2.5">Please select at least one option to continue.</p>
                  )}
                </div>

                {/* Product of Interest */}
                <div>
                  <label className={labelCls}>Product of Interest *</label>
                  <div className="relative">
                    <select name="product" value={formData.product} onChange={handleChange} required data-testid="contact-product-select" className={selectCls}>
                      <option value="">Select FlexiOss product...</option>
                      <option value="FlexiOss Ortho">FlexiOss® Ortho — Orthopedic</option>
                      <option value="FlexiOss Dent">FlexiOss® Dent — Dental / Maxillofacial</option>
                      <option value="FlexiOss Vet">FlexiOss® Vet — Veterinary</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Size + Pieces (show only after product selected) */}
                {formData.product && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Size {formData.requestSamples && '*'}</label>
                      <div className="relative">
                        <select name="size" value={formData.size} onChange={handleChange} required={formData.requestSamples} data-testid="contact-size-select" className={selectCls}>
                          <option value="">Select size...</option>
                          {sizes.map((s) => (<option key={s} value={s}>{s}</option>))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>No. of Pieces {formData.requestSamples && '*'}</label>
                      <input name="pieces" type="number" min="1" value={formData.pieces} onChange={handleChange} required={formData.requestSamples} data-testid="contact-pieces-input" placeholder="e.g. 10" className={inputCls} />
                    </div>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label className={labelCls}>Full Name *</label>
                  <input name="name" value={formData.name} onChange={handleChange} required data-testid="contact-name-input" placeholder="Dr. Juan Dela Cruz" className={inputCls} />
                </div>

                {/* Hospital / Clinic */}
                <div>
                  <label className={labelCls}>Hospital / Clinic Affiliation *</label>
                  <input name="clinic" value={formData.clinic} onChange={handleChange} required data-testid="contact-clinic-input" placeholder="e.g. St. Luke's Medical Center" className={inputCls} />
                </div>

                {/* Email + Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="contact-email-input" placeholder="doctor@hospital.com" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Phone *</label>
                    <input name="phone" type="tel" value={formData.phone} onChange={handleChange} required data-testid="contact-phone-input" placeholder="+63 XXX XXX XXXX" className={inputCls} />
                  </div>
                </div>

                {/* Specialty (auto-filled, still editable) */}
                <div>
                  <label className={labelCls}>Specialty</label>
                  <div className="relative">
                    <select name="specialty" value={formData.specialty} onChange={handleChange} data-testid="contact-specialty-select" className={selectCls}>
                      <option value="">Select specialty...</option>
                      <option value="Orthopedics">Orthopedics</option>
                      <option value="Dentistry / Maxillofacial">Dentistry / Maxillofacial</option>
                      <option value="Veterinary Medicine">Veterinary Medicine</option>
                      <option value="Other">Other</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* Specialty — Other (required free-text) */}
                {formData.specialty === 'Other' && (
                  <div>
                    <label className={labelCls}>Please specify your specialty *</label>
                    <input name="specialtyOther" value={formData.specialtyOther} onChange={handleChange} required data-testid="contact-specialty-other-input" placeholder="e.g. Neurosurgery, ENT, Plastic Surgery" className={inputCls} />
                  </div>
                )}

                {/* Practice / Institutional Verification Address — only when requesting a clinical evaluation kit / samples */}
                {formData.requestSamples && (
                <div className="rounded-none border border-slate-700/60 bg-slate-900/40 p-4 space-y-4" data-testid="contact-address-section">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-blue-400" />
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-300">Practice or Institutional Verification Address *</p>
                  </div>
                  <div>
                    <label className={labelCls}>Region *</label>
                    <div className="relative">
                      <select name="region" value={formData.region} onChange={handleChange} required data-testid="contact-region-select" className={selectCls}>
                        <option value="">Select region...</option>
                        {PH_REGIONS.map((r) => (<option key={r} value={r}>{r}</option>))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Province *</label>
                      <div className="relative">
                        <select name="province" value={formData.province} onChange={handleChange} required disabled={!formData.region} data-testid="contact-province-select" className={`${selectCls} disabled:opacity-50 disabled:cursor-not-allowed`}>
                          <option value="">{formData.region ? 'Select province...' : 'Select region first'}</option>
                          {provincesOf(formData.region).map((p) => (<option key={p} value={p}>{p}</option>))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>City / Municipality *</label>
                      <div className="relative">
                        <select name="city" value={formData.city} onChange={handleChange} required disabled={!formData.province} data-testid="contact-city-select" className={`${selectCls} disabled:opacity-50 disabled:cursor-not-allowed`}>
                          <option value="">{formData.province ? 'Select city / municipality...' : 'Select province first'}</option>
                          {citiesOf(formData.region, formData.province).map((c) => (<option key={c} value={c}>{c}</option>))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Postal Code *</label>
                      <input name="postal_code" inputMode="numeric" value={formData.postal_code} onChange={handleChange} required data-testid="contact-postal-input" placeholder="e.g. 3000" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Street / Bldg / Barangay *</label>
                      <input name="street_address" value={formData.street_address} onChange={handleChange} required data-testid="contact-street-input" placeholder="Lot 2 Blk 7, Brgy. ..." className={inputCls} />
                    </div>
                  </div>
                </div>
                )}

                {/* Message (auto-prefilled) */}
                <div>
                  <label className={labelCls}>Message</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} rows={3} data-testid="contact-message-textarea" placeholder="Tell us about your requirements..." className={`${inputCls} resize-none`} />
                </div>

                {/* NDA Requirement */}
                <label className="flex items-start gap-3 cursor-pointer group rounded-none border border-blue-500/30 bg-blue-500/5 p-3">
                  <input type="checkbox" name="consent_nda" checked={formData.consent_nda} onChange={(e) => setFormData(prev => ({ ...prev, consent_nda: e.target.checked }))} required data-testid="contact-nda-checkbox" className="mt-0.5 w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500/30 flex-shrink-0 accent-blue-500" />
                  <span className="text-xs text-slate-300 leading-relaxed">
                    I have read and agree to the{' '}
                    <a href="/non-disclosure" target="_blank" rel="noopener noreferrer" data-testid="contact-nda-link" className="font-semibold text-blue-400 underline underline-offset-2 hover:text-blue-300">Non-Disclosure Agreement (NDA)</a>.
                    I understand that all pricing, technical data, and sample materials shared are strictly confidential. *
                  </span>
                </label>

                {/* Data Privacy Consent */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" name="consent_privacy" checked={formData.consent_privacy} onChange={(e) => setFormData(prev => ({ ...prev, consent_privacy: e.target.checked }))} required data-testid="contact-privacy-checkbox" className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500/30 flex-shrink-0 accent-blue-500" />
                  <span className="text-xs text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                    I consent to the collection and processing of my personal data in accordance with <strong className="text-slate-300">Republic Act No. 10173 (Data Privacy Act of 2012)</strong> and its Implementing Rules and Regulations. I understand that my information will be used solely for the purpose of processing this request and will be handled in compliance with Philippine data privacy laws. *
                  </span>
                </label>

                {/* Clinical Report Commitment — only applicable when requesting samples */}
                {formData.requestSamples && (
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input type="checkbox" name="consent_clinical_report" checked={formData.consent_clinical_report} onChange={(e) => setFormData(prev => ({ ...prev, consent_clinical_report: e.target.checked }))} required data-testid="contact-report-checkbox" className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500/30 flex-shrink-0 accent-blue-500" />
                    <span className="text-xs text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                      By requesting a sample, I commit to <strong className="text-slate-300">test the product and provide a detailed clinical report</strong> of the case with proper documentation, including clinical observations, imaging, and outcomes, to support ongoing research and development. *
                    </span>
                  </label>
                )}

                {/* Submit */}
                {!noneSelected && consentBlocked && (
                  <p data-testid="contact-consent-hint" className="-mt-1 text-center text-[11px] text-amber-300/90">Please accept the required agreement(s) above to enable submission.</p>
                )}
                <button type="submit" disabled={submitDisabled} data-testid="contact-submit-btn" className="w-full text-white font-bold uppercase tracking-[0.1em] py-3.5 rounded-none text-sm shadow-2xl transition-all hover:scale-[1.01] flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed" style={{ backgroundColor: accentColor, boxShadow: `0 10px 40px -10px ${accentColor}80` }}>
                  {status === 'loading' ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="w-4 h-4" /> {submitLabel}</>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
